/**
 * Pagination Component
 *
 * A pagination component for navigating through pages of data.
 */

import { cn } from '@/utils';
import { Logger, LogCategory } from '@/utils/logger';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button, IconButton } from './Button';
import { Select } from './Select';
import type { PaginationState } from '@/types';
import { PAGINATION_CONFIG } from '@/constants';

// ============================================================================
// Types
// ============================================================================

export interface PaginationProps {
  pagination: PaginationState;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  showPageSizeSelector?: boolean;
  showPageInfo?: boolean;
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export function Pagination({
  pagination,
  onPageChange,
  onPageSizeChange,
  showPageSizeSelector = true,
  showPageInfo = true,
  className,
}: PaginationProps) {
  const { page, pageSize, totalItems, totalPages } = pagination;

  const startItem = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalItems);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
      Logger.debug(LogCategory.UI, 'Page changed', 'Pagination', {
        from: page,
        to: newPage,
        totalPages,
      });
      onPageChange(newPage);
    }
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPageSize = parseInt(e.target.value, 10);
    Logger.debug(LogCategory.UI, 'Page size changed', 'Pagination', {
      from: pageSize,
      to: newPageSize,
    });
    onPageSizeChange?.(newPageSize);
  };

  // Generate page numbers to display
  const getPageNumbers = (): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (page > 3) {
        pages.push('ellipsis');
      }

      // Show pages around current
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (page < totalPages - 2) {
        pages.push('ellipsis');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  if (totalPages === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex flex-col sm:flex-row items-center justify-between gap-4',
        className
      )}
    >
      {/* Page Info */}
      {showPageInfo && (
        <div className="text-sm text-slate-600">
          Showing <span className="font-medium">{startItem}</span> to{' '}
          <span className="font-medium">{endItem}</span> of{' '}
          <span className="font-medium">{totalItems}</span> results
        </div>
      )}

      <div className="flex items-center gap-4">
        {/* Page Size Selector */}
        {showPageSizeSelector && onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Show</span>
            <Select
              value={String(pageSize)}
              onChange={handlePageSizeChange}
              options={PAGINATION_CONFIG.PAGE_SIZE_OPTIONS.map((size) => ({
                value: String(size),
                label: String(size),
              }))}
              size="sm"
              fullWidth={false}
              className="w-20"
            />
          </div>
        )}

        {/* Page Navigation */}
        <div className="flex items-center gap-1">
          {/* First Page */}
          <IconButton
            icon={<ChevronsLeft className="h-4 w-4" />}
            variant="ghost"
            size="sm"
            onClick={() => handlePageChange(1)}
            disabled={page === 1}
            aria-label="First page"
          />

          {/* Previous Page */}
          <IconButton
            icon={<ChevronLeft className="h-4 w-4" />}
            variant="ghost"
            size="sm"
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            aria-label="Previous page"
          />

          {/* Page Numbers */}
          <div className="flex items-center gap-1">
            {getPageNumbers().map((pageNum, index) =>
              pageNum === 'ellipsis' ? (
                <span
                  key={`ellipsis-${index}`}
                  className="px-2 text-slate-400"
                >
                  ...
                </span>
              ) : (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => handlePageChange(pageNum)}
                  className="min-w-[2rem]"
                >
                  {pageNum}
                </Button>
              )
            )}
          </div>

          {/* Next Page */}
          <IconButton
            icon={<ChevronRight className="h-4 w-4" />}
            variant="ghost"
            size="sm"
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
            aria-label="Next page"
          />

          {/* Last Page */}
          <IconButton
            icon={<ChevronsRight className="h-4 w-4" />}
            variant="ghost"
            size="sm"
            onClick={() => handlePageChange(totalPages)}
            disabled={page === totalPages}
            aria-label="Last page"
          />
        </div>
      </div>
    </div>
  );
}
